// bounty
import {now} from '@acng/frontend-bounty/timing/now.js';
import {parse} from '@acng/frontend-bounty/std/date.js';
import {get, set} from '@acng/frontend-bounty/dom/attribute.js';
import {append} from '@acng/frontend-bounty/dom/template.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {setTimeout} from '@acng/frontend-bounty/dom/window.js';
import {isConnected, isHTMLElement} from '@acng/frontend-bounty/dom/type.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {TEMPLATE} from '@acng/frontend-bounty/dom/element.js';
// stargazer
import {AttributeLink, AttributeSetup, DATA_TRANSCLUDE, template} from '@acng/frontend-stargazer';
// nova
import {getSequence} from '../service/sequence';
import {TIMING_END, TIMING_START} from './common.js';

/**
 * Set the *sequence* to be used by
 *
 * - [Slideshow](../element/slideshow.js)
 * - [Pagination](../element/pagination.js)
 * - [Scene Title](./scene-title.js)
 *
 * for the element **and** the subtree.
 *
 * @example
 * ```html
 * <section seqence-name="slideshow1">
 *   <h1 data-scene-title></h1>
 *   <nova-slideshow></nova-slideshow>
 * </section>
 * ```
 *
 * @see [Sequence](../service/sequence.js)
 * @group DOM Attribute
 */
export const SEQUENCE_NAME = 'sequence-name';

AttributeSetup(SEQUENCE_NAME, (attributeName) => {
  const MINIMUM_DURATION = 1000;

  AttributeLink(attributeName, (element, sequenceName) => {
    if (isHTMLElement(element, TEMPLATE)) {
      const start = parse(get(element, TIMING_START), -Infinity);
      const end = parse(get(element, TIMING_END), Infinity);
      const time = now();
      const timeUntilStart = start - time;
      const timeUntilEnd = end - time;
      const show = () => {
        const sequence = getSequence(sequenceName);
        if (isConnected(element)) {
          DEBUG: if (debug(element)) console.debug('append scene to sequence', {sequence, element});
          append(template(sequence), element);
        }
      };
      const hide = () => {
        DEBUG: if (debug(element)) console.debug('remove scene from sequence', {element});
        remove(element);
      };
      DEBUG: if (debug(element)) console.debug('template is intended to be added to sequence', {element});

      if (timeUntilEnd < MINIMUM_DURATION) {
        DEBUG: if (debug(element)) console.debug('the remaining time is too short', {element});
        return;
      }

      return () => {
        DEBUG: if (debug(element)) console.debug('connect', {element, timeUntilStart, timeUntilEnd});
        if (timeUntilStart > 0) {
          setTimeout(show, timeUntilStart);
        } else {
          show();
        }

        setTimeout(hide, timeUntilEnd);
      };
    }
  });

  return (element) => {
    if (isHTMLElement(element, TEMPLATE)) {
      DEBUG: if (debug(element)) console.log('set [data-transclude]', {element});

      // It should be clear that the `[data-transclude]` only has an effect when nested in a looked up template,
      // and has **no effect** when directly looked up.
      // It may be compiled via transclude and then not be compiled again when directly looked up.
      set(element, DATA_TRANSCLUDE);
    }
  };
});
